import { cryptaniumVaultContractConfig } from "@config/contractsConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { getGainApy } from "@services/index";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useMemo } from "react";
import { useIdle } from "react-use";
import { numberToCurrency } from "utils";
import { erc20Abi, formatUnits } from "viem";
import { useReadContract } from "wagmi";
import useDappChain from "./useDappChain";
import useTVL from "./useTVL";
import { mainnet } from "viem/chains";

const UNIVERSE_API_ENDPOINT = process.env.NEXT_PUBLIC_UNIVERSE_API_URL;

const useCryptaniumVaultData = () => {
    const tvl = useTVL();
    const isIdle = useIdle(150e3);
    const { chain } = useDappChain();

    const { data: apyData, isLoading: isLoadingApy } = useQuery({
        queryKey: ["gainApy"],
        queryFn: async () => {
            const { data } = await getGainApy();
            return data || {};
        },
        enabled: !isIdle,
        staleTime: 60_000,
        refetchInterval: 60_000,
    });

    const ethPrice = useMemo(() => {
        if (tvl.tvl?.value && tvl.tvl.usdTvl) {
            return tvl?.tvl?.usdTvl / tvl.tvl?.value;
        }
        return 0;
    }, [tvl]);

    const { data: contractTotalAssets, isLoading } = useReadContract({
        address: cryptaniumVaultContractConfig[mainnet.id]?.interactionPool.address,
        abi: cryptaniumVaultContractConfig[mainnet.id]?.interactionPool.abi,
        functionName: "totalAssets",
        chainId: chain.id,
        query: {
            enabled: !isIdle && !!cryptaniumVaultContractConfig[mainnet.id],
            staleTime: 60_000,
            refetchInterval: 60_000,
        },
    });

    const { data: underlyingAsset } = useReadContract({
        address: cryptaniumVaultContractConfig[mainnet.id]?.interactionPool.address,
        abi: cryptaniumVaultContractConfig[mainnet.id]?.interactionPool.abi,
        functionName: "asset",
        chainId: chain.id,
        query: {
            enabled: !!cryptaniumVaultContractConfig[mainnet.id],
        },
    });

    const { data: assetDecimals } = useReadContract({
        address: underlyingAsset as `0x${string}`,
        abi: erc20Abi,
        functionName: "decimals",
        chainId: chain.id,
        query: {
            enabled: !!underlyingAsset,
        },
    });

    const data = useMemo(() => {
        if (contractTotalAssets === undefined || contractTotalAssets === null) {
            return null;
        }

        try {
            const decimals = assetDecimals || 18;
            const normalized = formatUnits(contractTotalAssets as bigint, decimals);
            return {
                totalAssets: {
                    normalized,
                },
            };
        } catch (err) {
            console.error("Error formatting contract data:", err);
            return null;
        }
    }, [contractTotalAssets, assetDecimals]);

    const { data: exchangeRateData, isLoading: isLoadingExchangeRate } = useQuery(
        {
            queryKey: ["eth-exchange"],
            queryFn: async () => {
                const r = await axios.get(
                    `${UNIVERSE_API_ENDPOINT}/rseth/exchangeRate/?lrtToken=ETH`
                );
                return r;
            },
            enabled: !isIdle,
            staleTime: 60_000,
            refetchInterval: 60_000,
        }
    );

    const totalTVL = useMemo(() => {
        if (
            exchangeRateData?.data?.value &&
            ethPrice &&
            data?.totalAssets?.normalized
        ) {
            return (
                exchangeRateData?.data?.value *
                ethPrice *
                Number(data.totalAssets.normalized)
            );
        }
        return null;
    }, [exchangeRateData?.data?.value, ethPrice, data?.totalAssets?.normalized]);

    const apy: number | null =
        apyData?.xgUSD && !isNaN(+apyData?.xgUSD) ? +apyData?.xgUSD : null;

    return {
        isLoadingAPYData: isLoadingApy || isLoading,
        isLoadingTotalStaked: isLoading || isLoadingExchangeRate,
        apy: apyData?.xgUSD,
        totalStaked: totalTVL,
        tvl: totalTVL,
        ethStaked: data?.totalAssets?.normalized,
        formattedApy: apy ? `${toFixedWithoutRounding(apy.toString(), 2)}%` : "-",
        formattedTvl:
            totalTVL && !isNaN(Number(totalTVL))
                ? `${numberToCurrency(totalTVL.toFixed(2), 1)}`
                : "-",
        formattedETHStaked: "",
    };
};

export default useCryptaniumVaultData;